import { httpsCallable, getFunctions } from 'firebase/functions';
import app from '../config/firebase';

const functions = getFunctions(app!);

export const callEnsureUserDoc = async () => {
  const fn = httpsCallable(functions, 'ensureUserDoc');
  const res = await fn({});
  return res.data as { email: string; displayName: string; role: string; activeProducts: string[] };
};

export const callSyncManagedUserCredentials = async (payload: {
  password: string;
  productIds?: Array<'vetaxis' | 'staffaxis' | 'noteaxis'>;
}) => {
  const fn = httpsCallable(functions, 'syncManagedUserCredentials');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    synced: Array<{
      productId: string;
      productUid: string;
    }>;
  };
};

export const callGetUserRole = async () => {
  const fn = httpsCallable(functions, 'getUserRole');
  const res = await fn({});
  return res.data as { role: string };
};

export const callAdminListUsers = async (payload: { limit?: number; startAfterEmail?: string }) => {
  const fn = httpsCallable(functions, 'adminListUsers');
  const res = await fn(payload);
  return res.data as { users: Array<{
    uid: string;
    email: string;
    displayName: string | null;
    role: string;
    activeProducts: string[];
    entitlements: Record<string, { plan: string; status: string; payfast?: any }>;
    createdAt: any;
  }> };
};

export const callAdminSetUserRole = async (payload: { targetUid: string; role: 'user' | 'admin' }) => {
  const fn = httpsCallable(functions, 'adminSetUserRole');
  const res = await fn(payload);
  return res.data as { ok: boolean };
};

export const callAdminGrantEntitlement = async (payload: {
  targetUid: string;
  productId: 'vetaxis' | 'staffaxis' | 'noteaxis' | 'suite';
  planId: string;
  status?: 'active' | 'trialing' | 'past_due' | 'canceled' | 'none';
  trialDays?: number;
}) => {
  const fn = httpsCallable(functions, 'adminGrantEntitlement');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    targetUid: string;
    productId: string;
    plan: string;
    status: string;
    trialEndsAt: string | null;
  };
};

export const callAdminDeleteUser = async (payload: { targetUid: string }) => {
  const fn = httpsCallable(functions, 'adminDeleteUser');
  const res = await fn(payload);
  return res.data as { ok: boolean; targetUid: string };
};

export const callAdminInviteClient = async (payload: {
  email: string;
  productId: 'vetaxis' | 'staffaxis' | 'noteaxis' | 'suite';
  planId: string;
  trialDays?: number;
  displayName?: string;
}) => {
  const fn = httpsCallable(functions, 'adminInviteClient');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    uid: string;
    isNewUser: boolean;
    emailSent: boolean;
    trialEndsAt: string;
  };
};

export const callAdminBackfillVetaxisBootstrap = async (payload: {
  targetUid?: string;
  limit?: number;
  dryRun?: boolean;
}) => {
  const fn = httpsCallable(functions, 'adminBackfillVetaxisBootstrap');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    dryRun: boolean;
    scanned: number;
    processedCount: number;
    skippedCount: number;
    errorCount: number;
    processed: Array<{
      uid: string;
      email?: string | null;
      plan?: string;
      status?: string;
      action?: 'processed' | 'would_process';
    }>;
    skipped: Array<{
      uid: string;
      email?: string | null;
      plan?: string;
      status?: string;
      reason: string;
    }>;
    errors: Array<{
      uid: string;
      error: string;
    }>;
  };
};

export const callAdminGetProductOperations = async (payload: {
  productId: 'vetaxis' | 'staffaxis' | 'noteaxis';
  limitUsers?: number;
}) => {
  const fn = httpsCallable(functions, 'adminGetProductOperations');
  const res = await fn(payload);
  return res.data as {
    productId: string;
    totalUsers: number;
    activeUsers: number;
    planCounts: Record<string, number>;
    users: Array<{
      uid: string;
      email: string | null;
      name: string | null;
      role: string | null;
      subscription: {
        planId: string;
        status: string;
        autoRenew: boolean;
      };
      createdAt?: any;
      updatedAt?: any;
    }>;
  };
};

export const callAdminGetVetaxisOperations = async (payload?: {
  limitClinics?: number;
  limitUsers?: number;
  clinicId?: string;
  includeUsage?: boolean;
}) => {
  const fn = httpsCallable(functions, 'adminGetVetaxisOperations');
  const res = await fn(payload || {});
  return res.data as {
    clinics: Array<{
      id: string;
      name: string;
      ownerId: string | null;
      subscriptionPlanId: string;
      planOverrides?: {
        price?: number;
        limits?: Record<string, number | string>;
      };
      baseSeatLimit: number;
      additionalSeatCount: number;
      activeMemberCount: number;
      createdAt?: any;
      updatedAt?: any;
    }>;
    users: Array<{
      uid: string;
      email: string | null;
      name: string | null;
      role: string | null;
      primaryClinicId: string | null;
      membershipCount: number;
      clinicMemberships: Array<{
        clinicId: string;
        role: string;
        status: string;
      }>;
      subscription: {
        planId: string;
        status: string;
      };
      createdAt?: any;
      updatedAt?: any;
    }>;
    clinicUsage: Record<string, {
      plan: {
        id: string;
        name: string;
        price: number;
        limits: Record<string, number | string>;
        overrides?: {
          price?: number;
          limits?: Record<string, number | string>;
        };
      };
      counts: {
        users: number;
        animals: number;
        clients: number;
      };
      monthly: {
        invoices: { currentMonth: number; previousMonth: number };
        clinicalJobs: { currentMonth: number; previousMonth: number };
      };
    }>;
    planCatalog: Record<string, {
      id: string;
      name: string;
      price: number;
      limits: Record<string, number | string>;
    }>;
    clinicUserCounts: Record<string, number>;
    totalClinics: number;
    totalUsers: number;
    orphanedUsers: number;
  };
};

export const callAdminUpdateVetaxisClinicOverrides = async (payload: {
  clinicId: string;
  clear?: boolean;
  overrides?: {
    price?: number | string;
    limits?: Partial<Record<
      'animals' | 'clients' | 'invoicesPerMonth' | 'clinicalJobsPerMonth' | 'usersIncluded' | 'maxUsers',
      number | string
    >>;
  };
}) => {
  const fn = httpsCallable(functions, 'adminUpdateVetaxisClinicOverrides');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    clinicId: string;
    planOverrides: {
      price?: number;
      limits?: Record<string, number | string>;
    };
  };
};

export const callAdminApplyVetaxisClinicBilling = async (payload: {
  clinicId: string;
}) => {
  const fn = httpsCallable(functions, 'adminApplyVetaxisClinicBilling');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    clinicId: string;
    ownerId: string;
    amount: number;
    source: 'override' | 'plan';
  };
};

export const callAdminGeneratePaymentLink = async (payload: {
  mode?: 'subscription' | 'custom';
  targetUid?: string;
  productId?: string;
  planId?: string;
  customAmount?: number;
  itemName?: string;
  notes?: string;
}) => {
  const fn = httpsCallable(functions, 'adminGeneratePaymentLink');
  const res = await fn(payload);
  return res.data as {
    linkId: string;
    linkToken: string;
    paymentUrl: string;
    amount: number;
    mode: 'subscription' | 'custom';
  };
};

export const callAdminManageSubscription = async (payload: {
  targetUid: string;
  productId: string;
  action: 'fetch' | 'cancel' | 'pause' | 'unpause';
  pauseCycles?: number;
}) => {
  const fn = httpsCallable(functions, 'adminManageSubscription');
  const res = await fn(payload);
  return res.data as { ok: boolean; result: any };
};

export const callAdminGetPaymentHistory = async (payload: { uid?: string; limit?: number }) => {
  const fn = httpsCallable(functions, 'adminGetPaymentHistory');
  const res = await fn(payload);
  return res.data as { payments: Array<{
    id: string;
    uid: string;
    pfPaymentId: string;
    paymentStatus: string;
    productId: string;
    planId: string;
    amountGross: string;
    amountFee: string;
    amountNet: string;
    itemName: string;
    emailAddress: string;
    createdAt: any;
    adminGenerated: boolean;
  }> };
};

export const callAdminGetAuditLogs = async (payload: {
  limit?: number;
  uid?: string;
  eventType?: string;
  outcome?: string;
  productId?: string;
  category?: string;
}) => {
  const fn = httpsCallable(functions, 'adminGetAuditLogs');
  const res = await fn(payload);
  return res.data as { logs: Array<{
    id: string;
    category?: string;
    eventType?: string;
    outcome?: string;
    actor?: Record<string, unknown> | null;
    target?: Record<string, unknown> | null;
    source?: Record<string, unknown> | null;
    details?: Record<string, unknown> | null;
    reason?: string | null;
    paymentStatus?: string | null;
    pfPaymentId?: string | null;
    createdAt?: any;
    receivedAt?: any;
    updatedAt?: any;
    rawPayload?: Record<string, unknown> | null;
  }> };
};

export const callAdminSendHelpDeskEmail = async (payload: {
  to: string;
  subject: string;
  html?: string;
  text?: string;
  projectId?: 'vetaxis' | 'staffaxis' | 'noteaxis';
  recipientName?: string;
  organization?: string;
}) => {
  const fn = httpsCallable(functions, 'adminSendHelpDeskEmail');
  const res = await fn(payload);
  return res.data as {
    ok: boolean;
    provider: string;
    logId: string;
    messageId: string | null;
  };
};
